import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import {
  SettingsNavGeneralIcon,
  SettingsNavBillingIcon,
  SettingsNavPrivacyIcon,
  SettingsNavSupportIcon,
  SettingsCloseIcon,
} from "assets/icons";
import { useNavigation } from "utils/navigate";
import { trackPage } from "utils/tracking";
import { SETTINGS_PATHS } from "./settingsPaths";
import { getSettingsReturn } from "./settingsReturn";
import {
  SettingsModalContainer,
  Sidebar,
  SidebarItem,
  Section,
  CloseButton,
} from "./SettingsModal.styles";

export const SETTINGS_TABS = {
  GENERAL: "general",
  BILLING: "billing",
  PRIVACY: "privacy",
  DOCUMENTS: "documents",
  SUPPORT: "support",
};

// Stand-in until the Figma icon is exported.
const DocumentsNavIcon = () => <FileText size={18} strokeWidth={1.5} />;

export const SettingsModal = ({ active, children }) => {
  const navigate = useNavigate();
  const { navigateToHome } = useNavigation();
  const activeRef = useRef(null);

  // On a phone the tabs scroll sideways; keep the open tab in view.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [active]);

  // The modal is a route, but closing it should not "go back" through the tabs.
  // It returns to the page the user was on before opening Settings. When that
  // page is the entry right before this one (tabs replace their entry, so it
  // normally is) step back to it, which leaves no duplicate history entry;
  // otherwise (a direct link, a reload) go to the remembered page, or home.
  const handleClose = () => {
    const returnTo = getSettingsReturn();
    const index = window.history.state?.idx;
    if (returnTo && returnTo.index != null && index === returnTo.index + 1) {
      navigate(-1);
    } else if (returnTo) {
      navigate(returnTo.path, { replace: true });
    } else {
      navigateToHome();
    }
  };

  // Switching tabs replaces the history entry, so the tabs never pile up.
  const goTo = (path) => () => {
    trackPage({ page: path });
    navigate(path, { replace: true });
  };

  const items = [
    {
      key: SETTINGS_TABS.GENERAL,
      label: "General",
      Icon: SettingsNavGeneralIcon,
      onSelect: goTo(SETTINGS_PATHS.general),
    },
    {
      key: SETTINGS_TABS.BILLING,
      label: "Billing",
      Icon: SettingsNavBillingIcon,
      onSelect: goTo(SETTINGS_PATHS.billing),
    },
    {
      key: SETTINGS_TABS.PRIVACY,
      label: "Privacy & security",
      Icon: SettingsNavPrivacyIcon,
      onSelect: goTo(SETTINGS_PATHS.privacy),
    },
    {
      key: SETTINGS_TABS.DOCUMENTS,
      label: "Documents",
      Icon: DocumentsNavIcon,
      onSelect: goTo(SETTINGS_PATHS.documents),
    },
    {
      key: SETTINGS_TABS.SUPPORT,
      label: "Support",
      Icon: SettingsNavSupportIcon,
      onSelect: goTo(SETTINGS_PATHS.support),
    },
  ];

  return (
    <SettingsModalContainer
      show
      centered
      animation={false}
      onHide={handleClose}
      aria-label="Settings"
    >
      <Modal.Body>
        <Sidebar aria-label="Settings sections">
          <div className="sidebar-title">Settings</div>
          <div className="sidebar-nav">
            {items.map(({ key, label, Icon, onSelect }) => (
              <SidebarItem
                key={key}
                ref={key === active ? activeRef : null}
                type="button"
                aria-current={key === active ? "page" : undefined}
                onClick={key === active ? undefined : onSelect}
              >
                <Icon />
                {label}
              </SidebarItem>
            ))}
          </div>
        </Sidebar>
        <Section>
          <div className="section-header">
            <CloseButton
              type="button"
              aria-label="Close settings"
              onClick={handleClose}
            >
              <SettingsCloseIcon />
            </CloseButton>
          </div>
          <div className="section-body">{children}</div>
        </Section>
      </Modal.Body>
    </SettingsModalContainer>
  );
};

SettingsModal.propTypes = {
  active: PropTypes.oneOf(Object.values(SETTINGS_TABS)).isRequired,
  children: PropTypes.node,
};
